import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Container,
  Button,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AccessTime, Person, CalendarToday, CheckCircle, Warning, ArrowBack } from '@mui/icons-material';
import '../Course/CoursePreview.css';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEmbedded } from '../../hooks/useEmbedded';
import { tokens } from '../../theme';
const HeaderPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  width: '100%',
}));

const LearningPathHeader = ({ lpath, contentStats = {}, enrollmentCount = 0, userRole, progress, enrolled, prerequisitesMet }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedded = useEmbedded();
  const showEnrollCount = [1, 11, 7].includes(userRole);

  // Check if we have parent course info in the location state
  const locationState = location.state || {};
  const parentCourseId = locationState.parentCourseId;
  const parentCourseName = locationState.parentCourseName || 'Parent Course';

  // Check if we have parent learning path info in the location state
  const parentLPathId = locationState.parentLPathId;
  const parentLPathName = locationState.parentLPathName || 'Parent Learning Path';

  const handleBackToParent = () => {
    const pathPrefix = isEmbedded ? '/embed' : '';
    if (parentCourseId) {
      navigate(`${pathPrefix}/learner/course/${parentCourseId}`);
    } else if (parentLPathId) {
      navigate(`${pathPrefix}/learner/lpath/${parentLPathId}`);
    }
  };

  const [expanded, setExpanded] = React.useState(false);
  const stripHtml = (value = '') => {
    if (!value) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = value;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  };
  
  return (
    <Box className="full-width-header">
      <HeaderPaper elevation={0} className="header-paper">
        <Box className="header-gradient">
          <Container>
            {(parentCourseId || parentLPathId) && (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBackToParent}
                  size="small"
                  sx={{
                    color: tokens.color.sidebarActive,
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: tokens.color.sidebarActive,
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    },
                    mb: 1
                  }}
                >
                  Back to {parentCourseId ? parentCourseName : parentLPathName}
                </Button>
              </Box>
            )}
            <Grid container spacing={2} alignItems="center">
              {/*
              <Grid item xs={12} md={8}>                
                <Typography variant="h3" component="h1" className="course-title" fontWeight="bold">
                  {lpath.title}
                </Typography>
                <Typography className="course-title">
                  Learning Path
                </Typography>
                
                {enrolled && prerequisitesMet === false && (
                  <Chip
                    icon={<Warning />}
                    label="Prerequisites Required"
                    variant="outlined"
                    color="warning"
                    className="prerequisite-chip"
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>
              */}
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Typography
                    variant="h3"
                    component="h1"
                    className="course-title"
                    fontWeight="bold"
                    sx={{ color: tokens.color.bgPrimary, lineHeight: 1.2 }}
                  >
                    {lpath.title}
                  </Typography>

                  <Chip
                    label= {progress?.progress == 100 ? "Learning Path  · Completed" : "Learning Path"}
                    size="small"
                    sx={{
                      backgroundColor:  progress?.progress == 100 ? 'rgba(21, 127, 91, .22)' : 'rgba(255, 255, 255, .13);',
                      borderRadius: 8,
                      color: progress?.progress == 100 ? tokens.color.positive : tokens.color.bgPrimary,
                      fontWeight: 600,
                      height: 24,
                    }}
                  />
                </Box>

                {lpath.description && (
                <Box
                  sx={{
                    mt: 2,
                    maxWidth: '760px',
                    maxHeight: expanded ? 300 : 'none',
                    overflowY: expanded ? 'auto' : 'hidden',
                    pr: expanded ? 1 : 0,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.82)',
                      display: '-webkit-box',
                      WebkitLineClamp: expanded ? 'unset' : 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: expanded ? 'visible' : 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.6,
                    }}
                  >
                    {stripHtml(lpath.description)}
                  </Typography>
                  {stripHtml(lpath.description).length > 140 && (
                    <Button
                      size="small"
                      onClick={() => setExpanded(!expanded)}
                      sx={{ color: tokens.color.bgPrimary, p: 0, minWidth: 'auto', mt: 0.5 }}
                    >
                      {expanded ? 'Show less' : 'Show more'}
                    </Button>
                  )}
                </Box>
              )}
              </Grid>
              <Grid item xs={12} md={4} className="enrollment-container">
                {showEnrollCount && (
                  <Chip
                    icon={<Person />}
                    label={`${enrollmentCount} Enrolled`}
                    variant="outlined"
                    className="enrollment-chip"
                    sx={{
                      width: 'fit-content',
                      maxWidth: '100%',
                      alignSelf: 'flex-start',
                      whiteSpace: 'nowrap',
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Container>
        </Box>
      </HeaderPaper>
    </Box>
  );
};

export default LearningPathHeader;